import axios from 'axios';
import { useState } from 'react';
import type { CatalogItemFields } from './CatalogItemModal';
import { invalidateCatalogItems } from './catalogItems';
import { formatPartName } from './formatNames';
import type { PartItem } from './partsRun';

// SAVING A PART OF THIS MANUAL TO THE PARTS DB, one at a time (the dialog) or all at once.

// The category a part is saved under: the model's, unless it declined to pick one — "OTHER"
// means none of them fits, which is not a category. A tool with no category still has one.
export const prefillCategory = (p: PartItem) => {
  const c = (p.category || '').trim();
  if (c && c.toUpperCase() !== 'OTHER') return c;
  return p.tool ? 'Tools' : '';
};

// What a part is saved as when nobody edits it: the same names the table shows, in the same
// order of authority, and its category.
export const defaultFields = (p: PartItem): CatalogItemFields => ({
  kind: p.tool ? 'tool' : 'part',
  nameEn: p.nameEn || formatPartName(p.name ?? ''),
  nameFr: p.nameFr || '',
  category: prefillCategory(p),
});

// ONE SAVE, with the part's drawing cropped from this manual and its size. The server answers
// with the existing row when the name is already on that category's shelf, so saving a part
// the database knows under the same name adds this manual's drawing and nothing else.
export async function savePartToDb(
  docId: string, p: PartItem, crop: string | null, fields: CatalogItemFields = defaultFields(p),
): Promise<number> {
  const { data } = await axios.post<{ id: number }>('/api/notice/catalog', {
    ...fields,
    schematic: crop,
    size: p.size,
    docId,
  });
  return data.id;
}

// EVERY PART NOT IN THE DATABASE, saved in turn (Louis 2026-11-03): one at a time, so
// two parts of one manual that are the same part land on one row and not on two racing ones.
// `onSaved` files each id as it lands; a part that fails is counted and the rest go on.
export function useSaveAllToDb(
  docId: string,
  onSaved: (at: number, id: number) => void,
) {
  const [progress, setProgress] = useState<{ done: number; of: number } | null>(null);
  const [failed, setFailed] = useState(0);
  const run = async (todo: { at: number; item: PartItem; crop: string | null }[]) => {
    if (progress || !todo.length) return;
    setFailed(0);
    setProgress({ done: 0, of: todo.length });
    let bad = 0;
    try {
      for (const [n, one] of todo.entries()) {
        try {
          onSaved(one.at, await savePartToDb(docId, one.item, one.crop));
        } catch {
          bad += 1;
        }
        setProgress({ done: n + 1, of: todo.length });
      }
    } finally {
      invalidateCatalogItems();
      setFailed(bad);
      setProgress(null);
    }
  };
  return { run, progress, failed };
}
